import { type MouseEvent } from "react";
import { PinIcon } from "lucide-react";

import { Button } from "@/components/ui/button";

import { usePinnedJobs } from "@/contexts/PinnedJobsContext";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";

const ICON_CLASS =
  "shrink-0 p-0 text-slate-400 opacity-0 hover:text-primary-900 group-hover:text-primary-600 group-hover:opacity-100";
const ICON_PINNED_CLASS = "text-primary-900 opacity-100 hover:text-primary-900";

const LOGO_CLASS =
  "absolute inset-0 rounded-none bg-transparent p-0 text-white opacity-0 hover:bg-black/55 hover:text-white group-hover/logo:bg-black/55 group-hover/logo:opacity-100";
const LOGO_PINNED_CLASS =
  "bg-black/35 text-white opacity-100 hover:bg-black/55 hover:text-white";

const VARIANT_STYLES = {
  small: {
    button: "h-5 w-5",
    icon: "h-3.5 w-3.5",
    base: ICON_CLASS,
    pinned: ICON_PINNED_CLASS,
  },
  medium: {
    button: "h-6 w-6",
    icon: "h-4 w-4",
    base: ICON_CLASS,
    pinned: ICON_PINNED_CLASS,
  },
  logo: {
    button: "h-full w-full",
    icon: "h-4 w-4",
    base: LOGO_CLASS,
    pinned: LOGO_PINNED_CLASS,
  },
} as const;

export type JobPinButtonVariant = keyof typeof VARIANT_STYLES;

// Pins/unpins a job for the current user. Hidden until the nearest `group`
// ancestor is hovered, unless the job is already pinned.
export const JobPinButton = ({
  className,
  companyId,
  jobId,
  screen,
  variant = "small",
}: {
  className?: string;
  companyId: string;
  jobId: string;
  screen: MIXPANEL_SCREENS;
  variant?: JobPinButtonVariant;
}) => {
  /* Database CRUD Operations */
  // Read + write
  const { isPinned, togglePin } = usePinnedJobs();

  /* Helpers */
  const pinned = isPinned(jobId);
  const styles = VARIANT_STYLES[variant];

  /* Handlers */
  const handleTogglePin = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    mixpanelUtils.trackButtonClick(pinned ? "Unpin job" : "Pin job", screen, {
      job_id: jobId,
      company_id: companyId,
    });
    togglePin(jobId);
  };

  /* Render */
  const render = () => (
    <Button
      type="button"
      variant="ghost"
      onClick={handleTogglePin}
      className={cn(
        styles.base,
        styles.button,
        pinned && styles.pinned,
        className,
      )}
    >
      <PinIcon className={cn(styles.icon, pinned && "fill-current")} />
    </Button>
  );

  return render();
};
